import { getContentImageUrls } from "../../lib/contentImages";

/**
 * Path for a post or event, optionally deep-linking a comment.
 * @param {"post"|"event"} contentType
 * @param {number|string} contentId
 * @param {number|string} [commentId]
 * @returns {string}
 */
export function buildContentSharePath(contentType, contentId, commentId) {
  const path = contentType === "event" ? `/event/${contentId}` : `/post/${contentId}`;
  if (commentId) {
    return `${path}?comment=${commentId}`;
  }
  return path;
}

/**
 * Absolute permalink for copying.
 * @param {"post"|"event"} contentType
 * @param {number|string} contentId
 * @param {number|string} [commentId]
 * @returns {string}
 */
export function buildContentShareUrl(contentType, contentId, commentId) {
  const path = buildContentSharePath(contentType, contentId, commentId);
  if (typeof window === "undefined" || !window.location?.origin) {
    return path;
  }
  return `${window.location.origin}${path}`;
}

/**
 * Org ids whose channels the current account can share into.
 * @param {object|null|undefined} user
 * @returns {number[]}
 */
export function getShareOrgIds(user) {
  if (!user) return [];
  if (user.type === "organization" && user.id != null) {
    const id = Number(user.id);
    return Number.isFinite(id) ? [id] : [];
  }
  return (user.affiliations || [])
    .filter((affiliation) => affiliation.status === "active" && affiliation.organization?.id != null)
    .map((affiliation) => Number(affiliation.organization.id))
    .filter(Number.isFinite);
}

/**
 * Other user id on a connection row.
 * @param {object} connection
 * @param {number|string} userId
 * @returns {number|null}
 */
export function peerIdFromConnection(connection, userId) {
  const sender = Number(connection?.sender_id);
  const receiver = Number(connection?.receiver_id);
  const self = Number(userId);
  if (Number.isFinite(sender) && sender === self) return Number.isFinite(receiver) ? receiver : null;
  if (Number.isFinite(receiver) && receiver === self) return Number.isFinite(sender) ? sender : null;
  return null;
}

/**
 * Display name for a user-like object.
 * @param {object|null|undefined} person
 * @returns {string}
 */
export function displayPersonName(person) {
  if (!person) return "User";
  if (person.name) return person.name;
  const full = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return full || person.username || "User";
}

/**
 * Snapshot stored in message.metadata for a shared post or event.
 * @param {object} options
 * @param {"post"|"event"} options.contentType
 * @param {object} options.content
 * @param {object} [options.comment]
 * @returns {object}
 */
export function buildShareMetadata({ contentType, content, comment }) {
  const contentId = content?.id;
  const permalink = buildContentSharePath(contentType, contentId, comment?.id);
  const images = getContentImageUrls(content);
  const snapshot = {
    content_type: contentType,
    content_id: contentId,
    author_id: content?.author_id ?? null,
    author_name: content?.author_name || "",
    author_pfp: content?.author_pfp || "",
    author_is_org: Boolean(content?.author_is_org),
    caption: contentType === "event" ? content?.title || "" : content?.caption || "",
    image_url: images[0] || "",
    permalink,
    mentions: Array.isArray(content?.mentions) ? content.mentions : [],
  };
  if (comment?.id) {
    snapshot.comment = {
      id: comment.id,
      author_id: comment.author_id ?? null,
      author_name: comment.author_name || "",
      author_pfp: comment.author_pfp || "",
      author_is_org: Boolean(comment.author_is_org),
      content: comment.content || "",
      mentions: Array.isArray(comment.mentions) ? comment.mentions : [],
    };
  }
  return snapshot;
}

/**
 * Parse share metadata from a chat message.
 * @param {object|null|undefined} message
 * @returns {object|null}
 */
export function parseShareMetadata(message) {
  if (!message) return null;
  const raw = message.metadata;
  if (!raw) return null;
  try {
    const meta = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!meta || typeof meta !== "object") return null;
    return meta;
  } catch {
    return null;
  }
}
